import * as React from "react";
import {FastAverageColor} from "fast-average-color";
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import styles from './PlaylistHeader.module.scss';
import {Playlist} from "../../../../store/api/types";
import {useBreakpoint} from "../../../../hooks/useBreakpoint";
import {ScrollingText} from "../../../common/scrollingText/ScrollingText";

interface DominantColor {
    r: number;
    g: number;
    b: number;
}

const calcDominantColor = async (imgSrc: string | undefined): Promise<DominantColor | null> => {
    try {
        if (imgSrc) {
            const img = document.createElement('img');
            img.crossOrigin = '';
            img.src = imgSrc;
            const fac = new FastAverageColor();
            const c = await fac.getColorAsync(img, {algorithm: 'sqrt', top: 10, left: 30, width: 50, height: 50});
            return {r: c.value[0], g: c.value[1], b: c.value[2]};
        }
    } catch (e) {
        console.log(e);
    }
    return null;
};

interface PlaylistHeaderProps extends React.PropsWithChildren {
    playlist: Playlist;
}

export const PlaylistHeader: React.FC<PlaylistHeaderProps> = ({playlist, children}) => {
    const [dominant, setDominant] = React.useState<DominantColor>();

    const breakpoint = useBreakpoint();
    const isDesktop = breakpoint === 'desktop';

    React.useEffect(() => {
        let cancelled = false;
        calcDominantColor(playlist?.img).then(c => {
            if (!cancelled && c) setDominant(c);
        });
        return () => { cancelled = true; };
    }, [playlist]);

    if (!playlist) {
        return <></>;
    }

    const dominantVars = dominant
        ? ({
            '--dom-r': dominant.r,
            '--dom-g': dominant.g,
            '--dom-b': dominant.b,
        } as React.CSSProperties)
        : undefined;

    return (
        <div
            className={`${isDesktop ? styles.playlistHeader__container : styles.responsive} ${dominant ? styles.playlistHeader__tinted : ''}`}
            style={dominantVars}
        >
            <div className={styles.playlistHeader__img}>
                {playlist.img
                    ? <img src={playlist.img} alt={playlist.name}/>
                    : <div className={styles.playlistHeader__imgPlaceholder}>
                        <QueueMusicIcon sx={{fontSize: 64, opacity: 0.4}}/>
                      </div>
                }
            </div>
            <div className={styles.playlistHeader__text}>
                <div className={styles.playlistHeader__eyebrow}>Playlist</div>
                <ScrollingText text={playlist.name} className={styles.playlistHeader__title} />
                <ScrollingText text={playlist.artist ?? ''} className={styles.playlistHeader__description} />
                {children && <div className={styles.playlistHeader__extras}>{children}</div>}
            </div>
        </div>
    );
}
